/**
 * Silent re-enrollment on every login — V7-01 §2.2.
 *
 * "A user who granted once never touches it again on that browser, including
 * after the founder regenerates VAPID keys — the next login self-heals."
 *
 * V6-01's behaviour was the opposite: a rotated key, a rotated endpoint or a
 * server-side prune all left the person subscribed in their own browser's
 * opinion and unreachable in ours, with no signal on either side. The only fix
 * was for every single user to find the settings button again.
 *
 * This runs once per authenticated mount, does nothing unless permission is
 * already `granted`, and never prompts or toasts — there is nothing to ask
 * (permission survives all three failures) and nothing worth interrupting
 * anyone about.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { ensureSubscribed } from './pushApi';

export function usePushAutoEnroll(): void {
  const { status, user } = useAuth();
  const qc = useQueryClient();
  // Per user id, so switching accounts in one tab re-runs it exactly once.
  const ranFor = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated' || !user) return;
    // Guests get no push surface at all (§2.1), so no silent work either.
    if (user.systemRole === 'guest') return;
    if (ranFor.current === user._id) return;
    ranFor.current = user._id;

    void ensureSubscribed().then((outcome) => {
      // A fresh subscription changes the device list the diagnostics panel and
      // the banner both read.
      if (outcome === 'resubscribed') {
        void qc.invalidateQueries({ queryKey: ['push-devices'] });
      }
    });
  }, [status, user, qc]);
}
